import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, ShoppingCart, BatteryCharging, Thermometer, Box, CheckCircle2, Shield, Wrench, TrendingDown, Share2, Mail, Copy, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { useCart } from '@/lib/cartContext';
import { trackEvent, EVENTS } from '@/lib/analytics';
import { trackViewProduct, trackAddToQuoteCart as trackGA4AddToCart } from '@/lib/ga4';
import { toast } from 'sonner';
import { estimateMonthlySavings, formatINR, formatINRCompact, loadAssumptions } from '@/lib/savings';
import { AssumptionsFooter } from '@/components/AssumptionsDrawer';
import { MiniCartDrawer } from '@/components/MiniCartDrawer';
import { fetchCatalogProduct } from '@/lib/catalogApi';

export default function ProductDetailPage() {
  const { sku } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [miniCartOpen, setMiniCartOpen] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);
  const [showPriceFactors, setShowPriceFactors] = useState(false);
  const { addItem } = useCart();
  const assumptions = loadAssumptions();

  useEffect(() => {
    fetchCatalogProduct(sku).then(({ product: data }) => {
      if (data) {
        // Normalize: ensure 'sku' field for cart compatibility
        setProduct({ ...data, sku: data.slug || data.sku || sku });
      }
      setLoading(false);
      if (data) {
        trackEvent(EVENTS.VIEW_PRODUCT, { sku });
        trackViewProduct(data, window.location.pathname);
      }
    }).catch(() => setLoading(false));
  }, [sku]);

  if (loading) return <div className="voltide-container py-20 text-center text-[#57534E]">Loading...</div>;
  if (!product || product.error) return <div className="voltide-container py-20 text-center"><h2 className="text-2xl font-bold">Product not found</h2><Link to="/products" className="text-[#0061DD] hover:underline mt-4 block">Back to Products</Link></div>;

  const savings = estimateMonthlySavings(product.battery_kwh, assumptions);
  const buyPrice = product.buy_from_price_ex_gst;
  const rentPerKwh = product.rent_from_inr_per_kwh_month;
  const rentMonthly = product.rent_from_inr_per_month;

  const handleAddToCart = (mode) => {
    addItem(product, mode);
    setLastAdded({ ...product, mode });
    setMiniCartOpen(true);
    trackEvent(EVENTS.ADD_TO_QUOTE_CART, { sku: product.sku, mode, source: 'product_detail' });
    trackGA4AddToCart(product, mode);
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      await navigator.share({ title: product.product_name, text: `Check out ${product.product_name} on Voltide`, url });
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`Voltide BESS: ${product.product_name}`);
    const buyText = buyPrice ? `Buy from ${formatINR(buyPrice)}` : 'Buy: Request quote';
    const rentText = rentPerKwh ? `Rent from ₹${Number(rentPerKwh).toFixed(2)}/kWh/mo` : 'Rent: Request quote';
    const body = encodeURIComponent(`Hi,\n\nI'd like to share this BESS product with you:\n\n${product.product_name}\n${product.pcs_kw} kW / ${product.battery_kwh} kWh\n${buyText} | ${rentText}\n\nView: ${window.location.href}\n\nBest regards`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <div data-testid="product-detail-page">
      <section className="pt-6">
        <div className="voltide-container">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0061DD] transition-colors" data-testid="back-to-products">
            <ArrowLeft className="w-4 h-4" /> Back to Products
          </Link>
        </div>
      </section>

      <section className="py-8 lg:py-14">
        <div className="voltide-container">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left: Image + Specs */}
            <div className="lg:col-span-3">
              <div className="bento-card overflow-hidden mb-6">
                <div className="aspect-[16/10] bg-[#F9F9F7] flex items-center justify-center p-10">
                  <img src={product.image} alt={product.product_name} className="max-h-full object-contain" />
                </div>
              </div>

              {/* Savings Panel */}
              <div className="bento-card p-5 mb-6 bg-emerald-50/30 border border-emerald-100" data-testid="savings-panel">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-base font-bold text-[#1A1A1A]">Your estimated savings</h3>
                </div>
                <p className="mono text-2xl font-bold text-emerald-700 mb-1">
                  {formatINRCompact(savings.low)} – {formatINRCompact(savings.high)}<span className="text-base font-medium text-emerald-600">/mo</span>
                </p>
                <p className="text-xs text-[#57534E] mb-3">vs diesel generator (DG replacement use case). Upload bills during RFQ for a tighter estimate.</p>
                <AssumptionsFooter />
              </div>

              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-[#1A1A1A] mb-3">Specifications</h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      { icon: BatteryCharging, label: 'PCS Power', value: `${product.pcs_kw} kW` },
                      { icon: BatteryCharging, label: 'Battery Energy', value: `${product.battery_kwh} kWh` },
                      ...(product.cooling ? [{ icon: Thermometer, label: 'Cooling', value: product.cooling }] : []),
                      ...(product.form_factor ? [{ icon: Box, label: 'Form Factor', value: product.form_factor }] : []),
                    ].map(spec => (
                      <div key={spec.label} className="flex items-center gap-3 p-3 rounded-xl bg-stone-50">
                        <spec.icon className="w-5 h-5 text-[#0061DD] shrink-0" />
                        <div>
                          <p className="text-xs text-[#A8A29E]">{spec.label}</p>
                          <p className="text-sm font-medium text-[#1A1A1A] mono">{spec.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Feature List */}
                {product.feature_list?.length > 0 && (
                  <div data-testid="feature-list">
                    <h2 className="text-lg font-bold text-[#1A1A1A] mb-3">Features</h2>
                    <ul className="space-y-2">
                      {product.feature_list.map((feat, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-[#57534E]">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                          {feat}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {product.use_case_tags?.length > 0 && (
                  <div>
                    <h2 className="text-lg font-bold text-[#1A1A1A] mb-2">Use Cases</h2>
                    <div className="flex flex-wrap gap-2">
                      {product.use_case_tags.map(tag => (
                        <Badge key={tag} className="rounded-full bg-blue-50 text-[#0061DD] border-blue-100 capitalize">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Sticky Pricing Panel */}
            <div className="lg:col-span-2">
              <div className="bento-card p-5 sticky top-20" data-testid="pricing-panel">
                {/* Header */}
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Badge className="rounded-full bg-stone-100 text-[#57534E] text-xs">{product.sku_type === 'BUNDLE' ? 'Bundle' : 'System'}</Badge>
                    {product.checkout_eligible && product.install_complexity === 'SIMPLE' && (
                      <Badge className="rounded-full bg-emerald-50 text-emerald-700 border-emerald-100 text-xs">Standard SKU</Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={handleShare} className="p-2 rounded-lg hover:bg-stone-50 text-[#A8A29E] hover:text-[#0061DD] transition-colors" title="Share" data-testid="share-btn">
                      <Share2 className="w-4 h-4" />
                    </button>
                    <button onClick={handleEmail} className="p-2 rounded-lg hover:bg-stone-50 text-[#A8A29E] hover:text-[#0061DD] transition-colors" title="Email this" data-testid="email-btn">
                      <Mail className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h1 className="text-xl font-bold text-[#1A1A1A] mt-2 mb-1">{product.product_name}</h1>
                <p className="text-sm text-[#57534E] mb-5">{product.pcs_kw} kW / {product.battery_kwh} kWh{product.form_factor ? ` · ${product.form_factor}` : ''}</p>

                {/* Buy Section */}
                {product.buy_price_visible !== false && (
                  <div className="p-4 rounded-xl bg-stone-50 border border-stone-200 mb-3" data-testid="buy-section">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm font-semibold text-[#1A1A1A]">Buy (from)</p>
                      <Badge className="rounded-full bg-stone-100 text-[#57534E] text-xs">Starting at</Badge>
                    </div>
                    {buyPrice ? (
                      <>
                        <p className="mono text-lg font-bold text-[#1A1A1A]">{formatINR(buyPrice)} <span className="text-xs font-normal text-[#A8A29E]">ex-GST</span></p>
                        <p className="text-[10px] text-[#A8A29E] mt-1">Prices are indicative and subject to change based on site conditions and final scope.</p>
                      </>
                    ) : (
                      <p className="text-sm font-medium text-[#0061DD]">Request quote for pricing</p>
                    )}
                    <Button onClick={() => handleAddToCart('buy')} className="w-full mt-3 rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white gap-2 text-sm" data-testid="add-to-cart-buy-btn">
                      <ShoppingCart className="w-4 h-4" /> Add to Quote Cart (Buy)
                    </Button>
                  </div>
                )}

                {/* Rent Section */}
                {product.rent_price_visible !== false && (
                  <div className="p-4 rounded-xl bg-blue-50/50 border border-blue-100 mb-3" data-testid="rent-section">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm font-semibold text-[#1A1A1A]">Rent (from)</p>
                      <Badge className="rounded-full bg-blue-50 text-[#0061DD] text-xs">RFQ-first</Badge>
                    </div>
                    {rentPerKwh ? (
                      <>
                        <p className="mono text-lg font-bold text-[#0061DD]">₹{Number(rentPerKwh).toFixed(2)}/kWh/mo <span className="text-xs font-normal text-[#57534E]">ex-GST</span></p>
                        {product.battery_kwh > 0 && (() => {
                          const eta = 0.95;
                          const minCommitPct = 0.70;
                          const daysPerYear = 365;
                          const annualUnits = product.battery_kwh * eta * daysPerYear;
                          const baseUnitsMonthlyFull = annualUnits / 12;
                          const minBillable = Math.round((minCommitPct * baseUnitsMonthlyFull) / 10) * 10;
                          return (
                            <p className="text-xs text-[#57534E] mt-0.5 flex items-center gap-1">
                              Minimum monthly billable discharge: {minBillable.toLocaleString('en-IN')} kWh
                              <span className="relative group inline-block cursor-help">
                                <Info className="w-3 h-3 text-[#A8A29E]" />
                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block w-48 p-2 text-[10px] text-white bg-[#1A1A1A] rounded-lg shadow-lg z-10">Minimum billing is based on 70% of baseline monthly units.</span>
                              </span>
                            </p>
                          );
                        })()}
                        {product.rent_assumptions_text && (
                          <p className="text-[10px] text-[#A8A29E] mt-2 leading-relaxed">{product.rent_assumptions_text}</p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm font-medium text-[#0061DD]">Request quote for rental pricing</p>
                    )}
                    <Button onClick={() => handleAddToCart('rent')} variant="outline" className="w-full mt-3 rounded-full border-[#0061DD] text-[#0061DD] hover:bg-blue-50 gap-2 text-sm" data-testid="add-to-cart-rent-btn">
                      <ShoppingCart className="w-4 h-4" /> Add to Quote Cart (Rent)
                    </Button>
                    {/* What happens next */}
                    <div className="mt-3 pt-3 border-t border-blue-100">
                      <p className="text-xs font-semibold text-[#57534E] mb-2">What happens next?</p>
                      <ol className="space-y-1.5 text-xs text-[#57534E]">
                        <li className="flex items-start gap-2"><span className="mono text-[#0061DD] font-bold shrink-0">1.</span> Submit RFQ with your site details</li>
                        <li className="flex items-start gap-2"><span className="mono text-[#0061DD] font-bold shrink-0">2.</span> Response within 48 hours (SLA)</li>
                        <li className="flex items-start gap-2"><span className="mono text-[#0061DD] font-bold shrink-0">3.</span> Site validation & technical assessment</li>
                        <li className="flex items-start gap-2"><span className="mono text-[#0061DD] font-bold shrink-0">4.</span> Formal quote with term & SLA options</li>
                      </ol>
                    </div>
                  </div>
                )}

                {/* Configure CTA */}
                <Link to="/quote" className="block mb-4">
                  <Button className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2 text-sm" data-testid="configure-for-site-btn">
                    Configure for my site <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>

                {/* Price factors */}
                <button onClick={() => setShowPriceFactors(!showPriceFactors)} className="flex items-center gap-1 text-xs text-[#A8A29E] hover:text-[#57534E] mb-3" data-testid="price-factors-toggle">
                  What affects pricing? {showPriceFactors ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                {showPriceFactors && (
                  <div className="text-xs text-[#57534E] space-y-1 p-3 rounded-lg bg-stone-50 mb-4" data-testid="price-factors">
                    <p>Final pricing depends on:</p>
                    <ul className="list-disc pl-4 space-y-0.5">
                      <li>Contract term & deposit structure</li>
                      <li>SLA level (response time, uptime guarantee)</li>
                      <li>Installation complexity & logistics</li>
                      <li>Site conditions & grid connectivity</li>
                    </ul>
                  </div>
                )}

                {/* What's Included */}
                <div className="pt-4 border-t border-stone-200">
                  <p className="text-sm font-semibold text-[#1A1A1A] mb-2">What's Included</p>
                  <ul className="space-y-1.5">
                    {['Battery system with BMS', 'Power Conversion System', 'Monitoring & EMS', 'Installation & commissioning', 'Warranty support'].map(item => (
                      <li key={item} className="flex items-center gap-2 text-xs text-[#57534E]">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MiniCartDrawer open={miniCartOpen} onClose={() => setMiniCartOpen(false)} lastItem={lastAdded} />
    </div>
  );
}
